import type { Paragraph, TextLine, TextRun } from '@/api/types/stowed-element'
import type { ParagraphBoxFit } from '@/lib/text/types/paragraph-box-fit'

/**
 * Splits `paragraphs` at a flat character `offset`, returning the same `{kept, overflow,
 * overflowIsFragment}` contract as `fitParagraphsToBox` so both fitters are interchangeable in the
 * chain reflow engine (WMPCM-7988).
 *
 * The offset follows the **shape-text** character convention — the one `paragraphsToPlainText`,
 * `buildCharRunMap`, `applyStyleToCharRange`, and `caretIndexAtShapeLocalPoint` in `shape-text.ts`
 * all share:
 * paragraphs joined by `\n`, and `textLines`/`textRuns` concatenated *without* any separator within
 * a paragraph. `isHidden` lines still contribute their characters here.
 *
 * That is deliberately **not** the text-frame convention in `lib/text/paragraph-plain-text.ts`,
 * which separates inner `textLines` with `\n` and drops hidden lines. Mirroring the wrong one
 * misplaces the split for any paragraph holding more than one `textLine` — which real yb360 data
 * does, since a `<PG>` may contain several `<TL>`. The load path now normalizes that away
 * (`unbakeShapeTextParagraphs`), so multi-line input is transient rather than the steady state —
 * but this function stays multi-line-tolerant on purpose: it is cheap, and it is what keeps the
 * two conventions from silently diverging again.
 *
 * The rectangle fitter gets this splitting for free from real DOM layout
 * (`splitContentEditableAtHeight`); a shape frame's capacity comes back from the scanline engine as
 * a character count instead, so the split has to be done structurally here.
 *
 * Paragraph-level properties (alignment, spacing, indentation, …) are preserved on *both* halves,
 * and run style is preserved by copying the run and replacing only its `textContent` — matching how
 * `fitParagraphsToBox` marks each split block with the source paragraph it came from
 * (`PARAGRAPH_SOURCE_ATTR`) so both fragments resolve their styling from that same paragraph.
 */
export function splitShapeTextParagraphsAtOffset(
  paragraphs: Paragraph[],
  offset: number
): ParagraphBoxFit {
  if (offset <= 0) return { kept: [], overflow: paragraphs, overflowIsFragment: false }

  // Start index, in the joined plain text, of the paragraph currently being considered.
  let start = 0

  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i]!
    const end = start + paragraphPlainTextLength(paragraph)

    // Landing exactly on a paragraph boundary is a clean break, not a fragment: everything up to
    // and including this paragraph fits, and the rest moves on whole.
    if (offset === end) {
      return {
        kept: paragraphs.slice(0, i + 1),
        overflow: paragraphs.slice(i + 1),
        overflowIsFragment: false,
      }
    }

    if (offset === start) {
      return {
        kept: paragraphs.slice(0, i),
        overflow: paragraphs.slice(i),
        overflowIsFragment: false,
      }
    }

    if (offset > end) {
      // `+ 1` for the `\n` this paragraph contributes to the join.
      start = end + 1
      continue
    }

    // Straddles this paragraph — split it and hand the trailing half to `overflow`.
    const { keptLines, overflowLines } = splitParagraphLines(paragraph, offset - start)

    return {
      kept: [...paragraphs.slice(0, i), { ...paragraph, textLines: keptLines }],
      overflow: [
        // A drop cap means "this paragraph's own first character becomes the cap" — true of the
        // kept half, which really does start the paragraph, but not of this overflow half, which is
        // a mid-paragraph continuation. Left alone, `computeDropCapPlacements` cannot tell the
        // difference and would draw a second, spurious cap at this fragment's own first character
        // in whatever frame it lands in next. Lines/Offset are left as-is — inert without an active
        // `dropCap`, and ready to reapply if a later edit ever makes this fragment a real paragraph
        // start again.
        { ...paragraph, textLines: overflowLines, dropCap: 'NONE' },
        ...paragraphs.slice(i + 1),
      ],
      overflowIsFragment: true,
    }
  }

  return { kept: paragraphs, overflow: [], overflowIsFragment: false }
}

function lineTextLength(line: TextLine): number {
  return (line.textRuns ?? []).reduce((sum, run) => sum + (run.textContent ?? '').length, 0)
}

function paragraphPlainTextLength(paragraph: Paragraph): number {
  return (paragraph.textLines ?? []).reduce((sum, line) => sum + lineTextLength(line), 0)
}

/**
 * Splits one paragraph's `textLines` at `localOffset`, an index into that paragraph's own
 * characters. Callers only reach this when the offset falls strictly inside the paragraph. Lines
 * concatenate with no separator, so unlike the paragraph walk there is no `+ 1` between them.
 */
function splitParagraphLines(
  paragraph: Paragraph,
  localOffset: number
): { keptLines: TextLine[]; overflowLines: TextLine[] } {
  const keptLines: TextLine[] = []
  const overflowLines: TextLine[] = []
  let start = 0
  let splitDone = false

  for (const line of paragraph.textLines ?? []) {
    if (splitDone) {
      overflowLines.push(line)
      continue
    }

    const end = start + lineTextLength(line)

    if (localOffset >= end) {
      keptLines.push(line)
      start = end

      if (localOffset === end) splitDone = true
      continue
    }

    if (localOffset === start) {
      overflowLines.push(line)
      splitDone = true
      continue
    }

    const { keptLine, overflowLine } = splitTextLine(line, localOffset - start)

    keptLines.push(keptLine)
    overflowLines.push(overflowLine)
    splitDone = true
  }

  return { keptLines, overflowLines }
}

/** Splits one line's runs at `localOffset`, breaking at most one run mid-text. */
function splitTextLine(
  line: TextLine,
  localOffset: number
): { keptLine: TextLine; overflowLine: TextLine } {
  const keptRuns: TextRun[] = []
  const overflowRuns: TextRun[] = []
  let start = 0

  for (const run of line.textRuns ?? []) {
    const text = run.textContent ?? ''
    const end = start + text.length

    if (end <= localOffset) {
      keptRuns.push(run)
    } else if (start >= localOffset) {
      overflowRuns.push(run)
    } else {
      const cut = localOffset - start

      keptRuns.push({ ...run, textContent: text.slice(0, cut) })
      overflowRuns.push({ ...run, textContent: text.slice(cut) })
    }

    start = end
  }

  return {
    keptLine: { ...line, textRuns: keptRuns },
    overflowLine: { ...line, textRuns: overflowRuns },
  }
}
